import { EVENT_SYSTEM } from '@/pages/game/live-events/model/vocab.js';

export const DAY = 86400000;
export const HOUR = 3600000;
export const iso = (ms) => new Date(ms).toISOString();

const pad = (n) => String(n).padStart(2, '0');

const utcLocal = (ms, time) => {
    const d = new Date(ms);
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${time}`;
};

const links = (...ids) => ids.map((configFileId) => ({ configFileId, parentBranchId: null }));

const task = (id, title, actor = 'AI', description = null) => ({
    id, title, description, actor, links: [], taskType: null, eventStartOffsetMinutes: null, required: true,
});

export const seedWorld = (gameId, now) => {
    const created = iso(now - 40 * DAY);
    const touched = iso(now - 2 * DAY);

    const event = (id, fields) => ({
        id, description: null, category: 'LIVEOPS', owner: 'GEOPLAY', featureId: null,
        scheduleKind: 'RECURRING', intervalValue: 1, intervalUnit: 'WEEK', startLocal: null, tzMode: 'FIXED', timezone: 'UTC',
        durationSeconds: null, recurrence: { end: { type: 'NEVER' } }, configLinks: [], segmentIds: [],
        target: null, guardrails: [], changes: [], rules: [], runMode: 'REVIEW', reviewLeadHours: null,
        aiSuggested: false, sortOrder: 0, createdAt: created, updatedAt: touched, ...fields,
    });

    const placement = (id, flywheelId, beatId, scheduleOverride = null) => ({
        id, flywheelId, laneId: null, kind: 'BEAT', beatId, startAt: iso(now - 30 * DAY), horizonDays: null,
        scheduleOverride, title: null, note: null, color: null, icon: null, sortOrder: 0, createdAt: created, updatedAt: created,
    });

    const list = (id, beatId, name, isDefault, items) => ({
        id, beatId, name, isDefault, sortOrder: isDefault ? 0 : 1,
        items: items.map((t, i) => ({ ...t, sortOrder: i })), createdAt: created, updatedAt: touched,
    });

    const snippet = (id, systemId, kind, title, body, rule = null) => ({
        id, system: EVENT_SYSTEM, systemId, kind, title, body, source: 'MANUAL', suggestedAction: 'NONE', rule, createdAt: created, updatedAt: created,
    });

    const run = (id, liveEventId, daysAgo, title, notes, results) => ({
        id, liveEventId, instanceId: null, ranAt: iso(now - daysAgo * DAY), title, notes, results, createdAt: iso(now - daysAgo * DAY), updatedAt: iso(now - daysAgo * DAY),
    });

    return {
        gameId,
        seq: 1000,
        flywheels: [
            { id: 'flywheel-core', name: 'Core Loop', description: 'The everyday loop of events, updates and tuning.', icon: 'repeat', color: '#6366f1', sortOrder: 0, seeded: true, lanes: [] },
            { id: 'flywheel-major', name: 'Major Updates', description: 'Content drops, features and seasons.', icon: 'rocket', color: '#8b5cf6', sortOrder: 1, seeded: true, lanes: [] },
            { id: 'flywheel-seasonal', name: 'Seasonal', description: 'Festivals and holiday events.', icon: 'gift', color: '#e25309', sortOrder: 2, seeded: false, lanes: [] },
        ],
        events: [
            event('liveEvent-halloween', {
                name: 'Halloween Event', description: 'Pumpkin pack sale and candy rewards.', sortOrder: 0,
                scheduleKind: 'ONE_TIME', intervalValue: null, intervalUnit: null, startLocal: utcLocal(now + 9 * DAY, '10:00'),
                durationSeconds: 259200, recurrence: null, configLinks: links('cfg-halloween'), runMode: 'DEFAULTS',
                target: { metricId: 'metric-revenue', direction: 'UP', mode: 'PERCENT', percent: 5, value: null, holdTolerancePct: null },
                rules: [{
                    id: 'r-1', severity: 'BLOCK', text: 'Never below 2.99', configFileId: 'cfg-halloween', path: 'pumpkinPack.priceUsd',
                    op: 'MIN', numValue: 2.99, strValues: [], knowledgeSnippetId: 'gameKnowledgeSnippet-floor',
                }],
            }),
            event('liveEvent-weekend-xp', {
                name: 'Weekend XP boost', description: 'Double XP across every mode over the weekend.', category: 'META', owner: 'SHARED',
                sortOrder: 1, startLocal: utcLocal(now - 60 * DAY, '00:00'), durationSeconds: 172800,
                recurrence: { byWeekday: ['SA'], end: { type: 'NEVER' } }, configLinks: links('cfg-progression'),
            }),
            event('liveEvent-store-refresh', {
                name: 'Weekly store refresh', description: 'New featured bundles and prices every Monday.', category: 'ECONOMY',
                sortOrder: 2, startLocal: utcLocal(now - 60 * DAY, '10:00'), durationSeconds: 604800,
                recurrence: { byWeekday: ['MO'], end: { type: 'NEVER' } }, configLinks: links('cfg-store-offers'),
            }),
            event('liveEvent-flash-sale', {
                name: 'Flash gem sale', description: 'A six-hour gem discount every few days.', category: 'ECONOMY', sortOrder: 3,
                intervalValue: 5, intervalUnit: 'DAY', startLocal: utcLocal(now - 28 * DAY, '18:00'), durationSeconds: 21600,
                configLinks: links('cfg-store-offers'),
            }),
            event('liveEvent-ranked', {
                name: 'Ranked season reset', description: 'Resets ranks and grants season rewards.', category: 'COMPETITIVE', sortOrder: 4,
                intervalUnit: 'MONTH', startLocal: utcLocal(now - 45 * DAY, '00:00'), durationSeconds: 86400,
                recurrence: { monthly: { mode: 'DAY_OF_MONTH', day: 1 }, end: { type: 'NEVER' } },
            }),
            event('liveEvent-diwali', {
                name: 'Diwali festival', description: 'Festive bundles and a lantern hunt for India.', sortOrder: 5,
                scheduleKind: 'ONE_TIME', intervalValue: null, intervalUnit: null, startLocal: utcLocal(now + 40 * DAY, '00:00'),
                timezone: 'Asia/Kolkata', durationSeconds: 432000, recurrence: null, configLinks: links('cfg-diwali'),
            }),
        ],
        placements: [
            placement('timelineItem-halloween', 'flywheel-seasonal', 'liveEvent-halloween'),
            placement('timelineItem-weekend-xp', 'flywheel-core', 'liveEvent-weekend-xp', { durationSeconds: 259200 }),
            placement('timelineItem-store', 'flywheel-core', 'liveEvent-store-refresh'),
            placement('timelineItem-flash', 'flywheel-core', 'liveEvent-flash-sale'),
            placement('timelineItem-ranked', 'flywheel-major', 'liveEvent-ranked'),
            placement('timelineItem-diwali', 'flywheel-seasonal', 'liveEvent-diwali'),
        ],
        taskLists: [
            list('liveEventTaskList-halloween', 'liveEvent-halloween', 'Halloween run', true, [
                task('liveEventTask-h1', 'Cut the pumpkin pack price by 20%', 'AI', 'Keep it above the floor'),
                task('liveEventTask-h2', 'Double the candy rewards'),
                task('liveEventTask-h3', 'Post the banner on Discord', 'MANUAL'),
                task('liveEventTask-h4', "How did last Halloween's pumpkin pack do?"),
            ]),
            list('liveEventTaskList-halloween-price', 'liveEvent-halloween', 'Price only', false, [
                task('liveEventTask-h5', 'Cut the pumpkin pack price by 20%', 'AI', 'Keep it above the floor'),
            ]),
            list('liveEventTaskList-xp', 'liveEvent-weekend-xp', 'Weekend boost', true, [
                task('liveEventTask-x1', 'Double the XP multiplier'),
                task('liveEventTask-x2', 'Raise the daily XP cap by 50%'),
                task('liveEventTask-x3', 'Tell the community team', 'MANUAL'),
            ]),
            list('liveEventTaskList-store', 'liveEvent-store-refresh', 'Weekly store refresh', true, [
                task('liveEventTask-s1', 'Make the starter bundle 15% cheaper'),
                task('liveEventTask-s2', 'Show the store refresh to Whales only'),
                task('liveEventTask-s3', 'Refresh the store banner art', 'MANUAL'),
            ]),
            list('liveEventTaskList-ranked', 'liveEvent-ranked', 'Season reset', true, [
                task('liveEventTask-r1', 'Reset the season rank floor to 0'),
                task('liveEventTask-r2', 'Announce the new season', 'MANUAL'),
            ]),
            list('liveEventTaskList-diwali', 'liveEvent-diwali', 'Diwali launch', true, [
                task('liveEventTask-d1', 'Give India players 10% off the lantern bundle'),
                task('liveEventTask-d2', 'Turn on the lantern hunt'),
                task('liveEventTask-d3', 'Keep it live for the first 3 days'),
                task('liveEventTask-d4', 'Brief the India CS team', 'MANUAL'),
            ]),
        ],
        configFiles: [
            {
                id: 'cfg-halloween', name: 'halloween.json', format: 'JSON', system: 'LIVEOPS', archived: false,
                content: { pumpkinPack: { priceUsd: 4.99, gems: 500 }, candyMultiplier: 1, candyPerWin: 10, bannerTheme: 'classic', eventTitle: 'Halloween' },
            },
            {
                id: 'cfg-progression', name: 'progression.json', format: 'JSON', system: 'PROGRESSION', archived: false,
                content: { xp: { multiplier: 1, dailyCap: 5000, perWin: 120 }, levelCurve: 'standard' },
            },
            {
                id: 'cfg-store-offers', name: 'store-offers.json', format: 'JSON', system: 'ECONOMY', archived: false,
                content: { featured: { bundleId: 'bundle_22', enabled: true }, starterBundle: { priceUsd: 2.99, gems: 300 }, gemPack: { priceUsd: 9.99, gems: 1200 } },
            },
            {
                id: 'cfg-diwali', name: 'diwali.json', format: 'JSON', system: 'LIVEOPS', archived: false,
                content: { lanternBundle: { priceUsd: 7.99, lanterns: 20 }, lanternHunt: { enabled: false, dropRate: 0.05 } },
            },
            {
                id: 'cfg-ranked', name: 'ranked-season.json', format: 'JSON', system: 'COMPETITIVE', archived: false,
                content: { season: { rankFloor: 3, rewardChest: 'gold' } },
            },
        ],
        segments: [
            { id: 'playerSegment-india', segmentName: 'India players', versionInfo: { parentEntityId: 'playerSegment-india' } },
            { id: 'playerSegment-whales', segmentName: 'Whales', versionInfo: { parentEntityId: 'playerSegment-whales' } },
            { id: 'playerSegment-new', segmentName: 'New players', versionInfo: { parentEntityId: 'playerSegment-new' } },
            { id: 'playerSegment-lapsed', segmentName: 'Lapsed 30 days', versionInfo: { parentEntityId: 'playerSegment-lapsed' } },
        ],
        snippets: [
            snippet('gameKnowledgeSnippet-floor', 'liveEvent-halloween', 'MONETIZATION', 'Pumpkin pack floor', 'Never sell the pumpkin pack below 2.99.',
                { fileId: 'cfg-halloween', key: 'pumpkinPack.priceUsd', op: 'MIN', value: 2.99, values: [] }),
            snippet('gameKnowledgeSnippet-candy', 'liveEvent-halloween', 'PROGRESSION', 'Candy rewards drive Halloween retention',
                'Doubling candy per win lifted D1 retention by 2 points last year.'),
            snippet('gameKnowledgeSnippet-xp-cap', 'liveEvent-weekend-xp', 'PROGRESSION', 'Daily XP cap ceiling',
                'The daily XP cap never goes above 10,000; the leaderboard breaks past it.',
                { fileId: 'cfg-progression', key: 'xp.dailyCap', op: 'MAX', value: 10000, values: [] }),
            snippet('gameKnowledgeSnippet-store', 'liveEvent-store-refresh', 'LIVEOPS', 'Bundles refresh every Monday at 10:00 UTC',
                'The store rotates on Monday morning, so weekend buyers still see the old set.'),
        ],
        runs: [
            run('liveEventRun-halloween-2025', 'liveEvent-halloween', 340, 'Halloween 2025', 'Pumpkin pack at 3.99 and candy per win doubled', 'Revenue up 6%, D1 retention up 2 points'),
            run('liveEventRun-xp-last', 'liveEvent-weekend-xp', 9, 'Weekend XP boost', 'XP doubled for the weekend', 'Sessions per player up 9%'),
            run('liveEventRun-store-w38', 'liveEvent-store-refresh', 14, 'Week 38 store refresh', 'Starter bundle 10% off', 'Store conversion 6.1%'),
        ],
        instances: [],
        plans: [],
        autoSettings: {
            gameId, enabled: false, occurrencesPerPlacement: 3, maxLeadDays: 30, maxOccurrencesPerRun: 100, lastRunAt: null, lastRunCreated: 0,
            processDates: false, maxDatesPerRun: 10, lastProcessedAt: null, lastRunProcessed: null,
        },
    };
};
